import { useState, useEffect } from 'react';
import WizardLayout from './WizardLayout';
import { useWizard } from '../../contexts/WizardContext';
import { authFetch } from '../../utils/auth';
import { useProfile } from '../../contexts/ProfileContext';
import { createMemoriesFromWizardStep, createWizardMemories, createWizardSummaryMemory } from '../../utils/wizardMemoryTemplates';

export default function StepBenefits({ navigate, finish, embedded = false, draft: externalDraft, setDraft: setExternalDraft }) {
  // Use either external draft (for embedded mode) or context draft (for wizard mode)
  const wizardContext = useWizard();
  const { refresh } = useProfile();
  
  const draft = embedded ? externalDraft : wizardContext.draft;
  const setDraft = embedded ? setExternalDraft : wizardContext.setDraft;
  
  const [currentBenefits, setCurrentBenefits] = useState(draft?.currentBenefits || []);
  const [, setSavedMemories] = useState([]);
  const [, setShowSaveConfirmation] = useState(false);
  
  // Keep local state in sync with draft changes (important for embedded mode)
  useEffect(() => {
    if (draft) {
      setCurrentBenefits(draft.currentBenefits || []);
    }
  }, [draft]);

  const benefitOptions = [
    'Hjelpestønad',
    'Grunnstønad',
    'Pleiepenger',
    'Omsorgspenger',
    'Omsorgslønn',
    'BPA',
    'Ingen av disse'
  ];

  const toggleBenefit = benefit => {
    let updatedBenefits;
    
    if (benefit === 'Ingen av disse') {
      // If "None of these" is selected, clear all other selections
      updatedBenefits = ['Ingen av disse'];
    } else {
      // If any other benefit is selected, remove "None of these" if present
      updatedBenefits = currentBenefits.includes(benefit)
        ? currentBenefits.filter(b => b !== benefit)
        : [...currentBenefits.filter(b => b !== 'Ingen av disse'), benefit];
    }
    
    setCurrentBenefits(updatedBenefits);
    
    // In embedded mode, update the draft immediately
    if (embedded) {
      setDraft({
        ...draft,
        currentBenefits: updatedBenefits
      });
    }
  };

  const skipWizard = async () => {
    try {
      // Create memories if user has provided any benefits
      if (currentBenefits && currentBenefits.length > 0) {
        const stepData = {
          currentBenefits,
          // Include child info from previous steps for entity mapping
          alias: draft?.child?.alias || '',
          birthYear: draft?.child?.birthYear || null
        };
        
        const memories = createMemoriesFromWizardStep('benefits', stepData);
        
        if (memories.length > 0) {
          const result = await createWizardMemories(memories);
          if (result.success) {
            setSavedMemories(memories);
            setShowSaveConfirmation(true);
            setTimeout(() => setShowSaveConfirmation(false), 3000);
          }
        }
        
        // Create wizard completion summary
        const summaryMemory = createWizardSummaryMemory({
          ...draft,
          currentBenefits
        });
        
        await createWizardMemories([summaryMemory]);
      }

      // Persist wizardSkipped to the database so it survives new sessions
      await authFetch('/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...draft,
          wizardSkipped: true,
          currentBenefits: currentBenefits || []
        })
      });
      
      // Wait until shared context is up-to-date
      await refresh();
      
      // Guard now sees correct flags
      navigate('/home', { replace: true });
    } catch (error) {
      console.error('Error skipping wizard:', error);
      alert('Det oppstod en feil. Vennligst prøv igjen.');
    }
  };

  const onNext = async ({ skip } = {}) => {
    if (embedded) return;
    
    if (skip) {
      await skipWizard();
      return;
    }
    
    try {
      // Create memories from current step data
      const stepData = {
        currentBenefits,
        // Include child info from previous steps for entity mapping
        alias: draft?.child?.alias || '',
        birthYear: draft?.child?.birthYear || null
      };
      
      const memories = createMemoriesFromWizardStep('benefits', stepData);
      
      if (memories.length > 0) {
        const result = await createWizardMemories(memories);
        if (result.success) {
          setSavedMemories(memories);
          setShowSaveConfirmation(true);
          setTimeout(() => setShowSaveConfirmation(false), 3000);
        }
      }
      
      // Create wizard completion summary
      const finalDraft = {
        ...draft,
        currentBenefits,
        profileComplete: true
      };
      
      const summaryMemory = createWizardSummaryMemory(finalDraft);
      await createWizardMemories([summaryMemory]);
      
      // Update local wizard state for flow continuity
      setDraft(finalDraft);
      
      // Call finish function (this will handle profile update/completion)
      finish(finalDraft);
    } catch (error) {
      console.error('Error in onNext:', error);
      alert('Det oppstod en feil. Vennligst prøv igjen.');
    }
  };

  // The form content
  const formContent = (
    <>
      <h2 className="text-xl font-semibold mb-6">Hvilke støtteordninger har dere i dag?</h2>
      


      <div className="mb-6">
        <p className="mb-3">Velg alle som passer *</p>
        <div className="flex flex-wrap gap-2">
          {benefitOptions.map(benefit => (
            <button
              key={benefit}
              type="button"
              onClick={() => toggleBenefit(benefit)}
              className={`px-3 py-1 rounded-full ${currentBenefits.includes(benefit) ? 'bg-primary text-white' : 'bg-gray-200'}`}
            >
              {benefit}
            </button>
          ))}
        </div>
      </div>


      {/* Completion message */}
      <div className="mt-6 p-4 bg-sage-50 border border-sage-200 rounded-lg">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-sage-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm">
            <p className="text-sage-800 font-medium mb-1">Nesten ferdig!</p>
            <p className="text-sage-700">
              Dette er det siste steget. Etter dette er du klar til å begynne å bruke Coord med personlig tilpasset hjelp.
            </p>
          </div>
        </div>
      </div>
    </>
  );

  // For embedded mode, just return the form content
  if (embedded) {
    return (
      <div className="border-b pb-8">
        {formContent}
      </div>
    );
  }

  // For wizard mode, wrap in WizardLayout
  return (
    <WizardLayout
      step={5}
      total={5}
      nextDisabled={currentBenefits.length === 0}
      onNext={onNext}
    >
      {formContent}
    </WizardLayout>
  );
}
